/**
 * Pet Mindful · Header
 *
 * Mega menú: abre en hover sobre el ítem y se cierra al salir del header, como
 * en el prototipo. Con teclado abre con Enter/Espacio (el enlace sigue
 * navegando con un clic real de mouse) y cierra con Escape.
 *
 * Menú lateral: bloquea el scroll de fondo, atrapa el foco y devuelve el foco
 * al botón que lo abrió.
 */
class PMHeader extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-pm-mega-trigger]'));
    this.drawer = this.querySelector('#pm-header-drawer');
    this.drawerOpener = this.querySelector('[data-pm-drawer-open]');
    this.openTrigger = null;

    this.bindMega();
    this.bindDrawer();

    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockScroll();
  }

  /* ---------------------------------------------------------------- mega -- */

  bindMega() {
    this.triggers.forEach((trigger) => {
      const panel = this.panelFor(trigger);
      if (!panel) return;

      trigger.addEventListener('mouseenter', () => this.openMega(trigger));
      trigger.addEventListener('focus', () => this.openMega(trigger));

      // El teclado no debe navegar al pulsar: primero despliega el panel.
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          if (this.openTrigger !== trigger) {
            event.preventDefault();
            this.openMega(trigger);
            const first = panel.querySelector('a');
            if (first) first.focus();
          }
        }
      });
    });

    // Se cierra al salir del header, no de cada ítem: el puntero tiene que
    // poder cruzar el hueco entre el ítem y el panel.
    this.addEventListener('mouseleave', () => this.closeMega());

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeMega();
    });
  }

  panelFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.querySelector('#' + CSS.escape(id)) : null;
  }

  openMega(trigger) {
    if (this.openTrigger === trigger) return;
    this.closeMega();

    const panel = this.panelFor(trigger);
    if (!panel) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.openTrigger = trigger;
  }

  closeMega() {
    if (!this.openTrigger) return;
    const panel = this.panelFor(this.openTrigger);
    if (panel) panel.hidden = true;
    this.openTrigger.setAttribute('aria-expanded', 'false');
    this.openTrigger = null;
  }

  /* -------------------------------------------------------------- drawer -- */

  bindDrawer() {
    if (!this.drawer) return;

    if (this.drawerOpener) {
      this.drawerOpener.addEventListener('click', () => this.openDrawer());
    }

    this.drawer.querySelectorAll('[data-pm-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });
  }

  openDrawer() {
    this.drawer.hidden = false;
    if (this.drawerOpener) this.drawerOpener.setAttribute('aria-expanded', 'true');
    this.lockScroll();

    const first = this.drawer.querySelector('a, button');
    if (first) first.focus();
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.hidden = true;
    if (this.drawerOpener) {
      this.drawerOpener.setAttribute('aria-expanded', 'false');
      this.drawerOpener.focus();
    }
    this.unlockScroll();
  }

  lockScroll() {
    document.body.classList.add('pm-no-scroll');
  }

  unlockScroll() {
    document.body.classList.remove('pm-no-scroll');
  }

  /* ------------------------------------------------------------- teclado -- */

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.drawer && !this.drawer.hidden) {
        this.closeDrawer();
      } else if (this.openTrigger) {
        const trigger = this.openTrigger;
        this.closeMega();
        trigger.focus();
      }
      return;
    }

    // Foco atrapado dentro del panel lateral mientras esta abierto
    if (event.key === 'Tab' && this.drawer && !this.drawer.hidden) {
      const focusables = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
}

if (!customElements.get('pm-header')) {
  customElements.define('pm-header', PMHeader);
}
